import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import { create } from 'zustand';

import { STANDARD_STALE_TIME } from '$/services/constants';
import {
  getHarmonies,
  getHarmonyById,
  getMaterials,
  type GetMaterialsPayload,
} from '$/services/usecases/materials';
import { type Material } from '$/services/usecases/materials/mapper/materials';

const useMaterialCache = create<{ materials: Map<string, Material> }>()(() => ({
  materials: new Map(),
}));

const cacheMaterials = (mats: Material[]) => {
  const newMaterials = new Map(useMaterialCache.getState().materials);
  mats.forEach((material) => newMaterials.set(material.id, material));
  useMaterialCache.setState({ materials: newMaterials });
};

const queryFn = async (payload: GetMaterialsPayload, signal: AbortSignal) => {
  const fetchAllMaterials = async () => {
    const { results, count } = await getMaterials(payload, signal);
    cacheMaterials(results);
    return { results, count };
  };

  const fetchMaterialsFromIds = async () => {
    const missingIds = new Set(
      payload.ids!.filter(
        (id) => !useMaterialCache.getState().materials.has(id),
      ),
    );

    if (missingIds.size !== 0) {
      const { results } = await getMaterials(
        { ids: [...missingIds], isDemo: payload.isDemo },
        signal,
      );
      cacheMaterials(results);
    }

    const cachedMaterials = payload.ids!.map(
      (id) => useMaterialCache.getState().materials.get(id)!,
    );
    return { count: cachedMaterials.length, results: cachedMaterials };
  };

  if (payload.ids) {
    return await fetchMaterialsFromIds();
  } else {
    return await fetchAllMaterials();
  }
};

export const infiniteMaterialsQuery = (
  payload: GetMaterialsPayload = {},
  disabled: boolean = false,
) =>
  infiniteQueryOptions({
    queryKey: ['materials', payload],
    queryFn: async ({ signal, pageParam }) => {
      const { skip } = pageParam;
      return queryFn({ ...payload, skip }, signal);
    },
    initialPageParam: { skip: 0 },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.count < lastPageParam.skip) {
        return undefined;
      }
      return {
        skip: lastPageParam.skip + (payload.limit ?? 50),
      };
    },
    placeholderData: keepPreviousData,
    staleTime: STANDARD_STALE_TIME,
    enabled: !disabled,
  });

export const materialsQuery = (
  payload: GetMaterialsPayload = {},
  opt: {
    keepPrevious?: boolean;
    disabled?: boolean;
    collectionIds?: string[];
  } = {},
) =>
  queryOptions({
    queryKey: ['materials', payload, opt],
    queryFn: ({ signal }) => queryFn(payload, signal),
    refetchOnWindowFocus: false,
    staleTime: STANDARD_STALE_TIME,
    placeholderData: opt.keepPrevious ? keepPreviousData : undefined,
    enabled: !opt.disabled,
  });

export const harmoniesQuery = (options?: { enabled?: boolean }) =>
  queryOptions({
    ...options,
    queryKey: ['materials', 'harmonies'],
    queryFn: ({ signal }) => getHarmonies(signal),
    staleTime: STANDARD_STALE_TIME,
  });

export const harmonyByIdQuery = (id: string) =>
  queryOptions({
    queryKey: ['materials', 'harmonies', id],
    queryFn: ({ signal }) => getHarmonyById(id, signal),
    staleTime: STANDARD_STALE_TIME,
  });
